import * as React from "react";
import "./PreferredPharmacyAutoComplete.css";
import "../../styles/FormInput.css";
import "../../styles/Buttons.css";
import { useRef } from "react";
import Client from "../../services/Client";
import { PharmacyDto, PreferredPharmacyCheckin } from "../../dtos/KioskDtos.dtos";
import { InputControl } from "../FormControls/InputControl";
import { IGenericsLang } from "../../services/Language/Types";
import { CustomCheckbox } from "../customFormComponents/CheckBoxGroup/Checkbox";

interface IPreferredPharmacyAutoCompleteProps {
    focusedInputName: string,
    isContactDirectoryEnabled: boolean,
    langCode: string,
    text: IGenericsLang,
    onChange(val: any): void,
    onFocus: (pipe: (value: any) => void, value: any, mask: string, id: string) => void,
    existingPreferredPharmacy?: PreferredPharmacyCheckin
}

export const PreferredPharmacyAutoComplete = (props: IPreferredPharmacyAutoCompleteProps) => {
    const pharmacySearchId = "pharmacySearchId";
    const container = useRef<HTMLDivElement>(undefined);
    const lockSearch = useRef(false);

    const [displayList, setDisplayList] = React.useState(false);
    const [preferredPharmacy, setPreferredPharmacy] = React.useState<PreferredPharmacyCheckin>(props.existingPreferredPharmacy);
    const [results, setResults] = React.useState<PharmacyDto[]>([]);
    const [enableExtendedGeographicalSearch, setEnableExtendedGeographicalSearch] = React.useState<boolean>(false);
    const [text, setText] = React.useState<string>("");

    const onFocus = (): void => {
        if (results.length > 0 && !displayList)
            setDisplayList(true);
        props.onFocus(value => onTextChange(value), text, "", pharmacySearchId)
    };

    const keyDown = (e: React.KeyboardEvent): void => {
        if (e.key === "Escape")
            setDisplayList(false);
    };

    const hideList = (): void => {
        window.setTimeout(() => {
            setDisplayList(false);
        }, 200);
    };

    const onRemoveSelectedPharmacy = (): void => {
        setPreferredPharmacy(null);
        setText("")
        props.onChange(null);
    }

    const onSelectItem = (item: PharmacyDto): void => {
        lockSearch.current = true;
        const selectedPreferredPharmacy = new PreferredPharmacyCheckin({
            id: item.id,
            name: item.name,
            address: item.address,
            phone: item.phone,
            fax: item.fax,
            banner: item.banner,
            isMylePharmacy: item.isMylePharmacy
        });
        setPreferredPharmacy(selectedPreferredPharmacy);
        setText(item.banner)
        setDisplayList(false);
        props.onChange(selectedPreferredPharmacy);
    };

    const onTextChange = (value: string): void => {
        if (lockSearch.current) {
            lockSearch.current = false;
            return;
        }

        if(value.length === 0) {
            setDisplayList(false);
            setResults([]);
        }

        setText(value);
        executeSearch(value);
    }

    const executeSearch = async (searchString?: string): Promise<void> => {
        try {
            const response = await Client.getPharmacyListFromMedfarDictionary(searchString, 5, props.isContactDirectoryEnabled, enableExtendedGeographicalSearch);

            if (!response.searchPharmacyResults || response.searchPharmacyResults.length === 0)
                return Promise.resolve();

            setResults(response.searchPharmacyResults);
            setDisplayList(response.searchPharmacyResults.length > 0);

            return Promise.resolve();
        } catch (error) {
            console.error("Error during search:", error);
            return Promise.reject(error);
        }
    };

    const getFaxAndPhoneDisplay = (phone: string, fax: string): string => {
        if (!phone && !fax)  
            return "";
        
        const displayParts: string[] = [];
        if (phone) 
            displayParts.push(`T: ${phone}`);
        if (fax) 
            displayParts.push(`F: ${fax}`);
        
        return displayParts.join(" | ");
    }

    return (
        <div ref={container} className="pharmacy-search-autocomplete">
            <div className="pharmacy-search-name-label">{props.text.labels.PharmacyNameLabel}</div>
            <InputControl
                value={text}
                onBlur={hideList}
                onFocus={onFocus}
                onKeyDown={keyDown}
                onChange={value => onTextChange(value)}
                focused={props.focusedInputName === pharmacySearchId}
            />
            {displayList && (
                <div className="pharmacy-results-container">
                    <ul className="pharmacy-results-list">
                        {results.map((pharmacy, index) => (
                            <li onClick={(_) => onSelectItem(pharmacy)} key={`pharmacy-${index}`}>
                                <div className="pharmacy-display">
                                    <strong className="pharmacy-name-display">{pharmacy.banner} - {pharmacy.name}</strong>
                                    <p>{pharmacy.address}</p>
                                    <p>{getFaxAndPhoneDisplay(pharmacy.phone, pharmacy.fax)}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {props.isContactDirectoryEnabled && (
                <div className="pharmacy-geographical-search-switch">
                    <CustomCheckbox 
                        label={props.text.labels.SearchAllCountriesAndStatesOrProvincesLabel}
                        checked={enableExtendedGeographicalSearch}
                        onClick={(checked) => setEnableExtendedGeographicalSearch(checked)}
                    />
                </div>
            )}
            {preferredPharmacy && (
                <div className="form-container-inline selected-pharmacy-display">
                    <div className="pharmacy-display">
                        <strong className="pharmacy-name-display">{preferredPharmacy.banner} - {preferredPharmacy.name}</strong>
                        <p>{preferredPharmacy.address}</p>
                        <p>{getFaxAndPhoneDisplay(preferredPharmacy.phone, preferredPharmacy.fax)}</p>
                    </div>
                    <div className="delete">
                        <span onClick={() => onRemoveSelectedPharmacy()}>
                            <i className="fas fa-times" />
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/PreferredPharmacyAutoComplete/PreferredPharmacyAutoComplete.tsx